import React, { useState, useEffect } from 'react';
import './CitationDrawer.css';

interface CitationDrawerProps {
  visible: boolean;
  citations: Array<{
    title?: string;
    url?: string;
    text?: string;
    cited_text?: string;
    sentence?: string;
  }>;
  selectedCitationIndex: number;
  onClose: () => void;
  onCitationSelect: (index: number) => void;
  mode?: 'inline' | 'drawer' | 'tooltip';
  anchorElement?: HTMLElement | null;
}

const CitationDrawer: React.FC<CitationDrawerProps> = ({
  visible,
  citations = [],
  selectedCitationIndex = 0,
  onClose,
  onCitationSelect,
  mode = 'drawer',
  anchorElement
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (visible) {
      setIsAnimating(true);
      // Add body scroll lock on mobile
      if (mode === 'drawer') {
        document.body.style.overflow = 'hidden';
      }
    } else {
      // Remove body scroll lock
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [visible, mode]);

  // Filter out null/undefined citations and ensure we have valid data
  const validCitations = citations.filter(citation => citation && typeof citation === 'object');
  const selectedCitation = validCitations[selectedCitationIndex];

  if (!visible || !selectedCitation || validCitations.length === 0) return null;

  const renderContent = () => {
    const content = selectedCitation.text || selectedCitation.cited_text || selectedCitation.sentence || '';
    
    // Clean up malformed markdown
    let cleanContent = content
      .replace(/^\*\*\s*/, '') // Remove leading **
      .replace(/\s*\*\*$/, '') // Remove trailing **
      .trim();

    // Process content to separate headings from description text
    const processedLines: string[] = [];
    let isFirst = true;

    if (cleanContent.includes('##')) {
      // Split by heading markers and process
      const parts = cleanContent.split(/(#{1,4}\s*)/);
      let currentHeadingLevel = '';

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();

        // Check if this part is a heading marker
        if (/^#{1,4}\s*$/.test(part)) {
          currentHeadingLevel = part;
          continue;
        }

        if (currentHeadingLevel && part) {
          // This is heading content - split at first sentence/period
          const headingMatch = part.match(/^([^.!?]*[.!?:]?)\s*(.*)/);
          if (headingMatch) {
            const headingText = headingMatch[1].trim();
            const remainingText = headingMatch[2].trim();

            // Add heading
            if (headingText) {
              const marginTop = isFirst ? '0' : '12px';
              const fontSize = currentHeadingLevel.startsWith('##') ? '16px' : '15px';
              processedLines.push(`<div class="citation-heading" style="font-weight: 600; color: #1f2937; font-size: ${fontSize}; margin: ${marginTop} 0 8px 0;">${headingText}</div>`);
              isFirst = false;
            }

            // Add remaining text as description
            if (remainingText) {
              processedLines.push(`<div class="citation-text" style="margin: 6px 0 0 0; line-height: 1.5; color: #4b5563;">${remainingText}</div>`);
            }
          }
          currentHeadingLevel = '';
        } else if (part && !currentHeadingLevel) {
          // Regular text without heading marker
          const marginTop = isFirst ? '0' : '6px';
          processedLines.push(`<div class="citation-text" style="margin: ${marginTop} 0 0 0; line-height: 1.5; color: #4b5563;">${part}</div>`);
          isFirst = false;
        }
      }
    } else {
      // No heading markers, treat as regular text
      processedLines.push(`<div class="citation-text" style="margin: 0; line-height: 1.5; color: #4b5563;">${cleanContent}</div>`);
    }

    return processedLines.join('');
  };

  // Inline mode - appears below the citation marker
  if (mode === 'inline' && anchorElement) {
    const rect = anchorElement.getBoundingClientRect();
    
    return (
      <>
        {/* Backdrop */}
        <div 
          className="citation-inline-backdrop"
          onClick={onClose}
        />
        
        {/* Inline citation card */}
        <div
          className={`citation-inline-card ${isAnimating ? 'animate-in' : ''}`}
          style={{
            position: 'fixed',
            top: rect.bottom + 8,
            left: Math.max(8, Math.min(rect.left, window.innerWidth - 320 - 8)),
            right: 8,
            zIndex: 10000,
            maxWidth: 'calc(100vw - 16px)',
            boxSizing: 'border-box'
          }}
        >
          <div className="citation-inline-header">
            <h3 className="citation-inline-title">
              {selectedCitation.title || 'Source'}
            </h3>
            {selectedCitation.url && (
              <a 
                href={selectedCitation.url}
                target="_blank"
                rel="noopener noreferrer"
                className="citation-inline-link"
                onClick={(e) => e.stopPropagation()}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15,3 21,3 21,9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            )}
            <button 
              className="citation-inline-close"
              onClick={onClose}
              aria-label="Close citation"
            >
              ×
            </button>
          </div>
          
          <div className="citation-inline-content">
            <div 
              dangerouslySetInnerHTML={{ __html: renderContent() }}
            />
          </div>

          {validCitations.length > 1 && (
            <div className="citation-inline-navigation">
              <button 
                className="citation-nav-btn"
                onClick={() => onCitationSelect(Math.max(0, selectedCitationIndex - 1))}
                disabled={selectedCitationIndex === 0}
              >
                ←
              </button>
              <span className="citation-counter">
                {selectedCitationIndex + 1} of {validCitations.length}
              </span>
              <button 
                className="citation-nav-btn"
                onClick={() => onCitationSelect(Math.min(validCitations.length - 1, selectedCitationIndex + 1))}
                disabled={selectedCitationIndex === validCitations.length - 1}
              >
                →
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  // Drawer mode - bottom sheet for mobile
  return (
    <>
      {/* Backdrop */}
      <div 
        className={`citation-drawer-backdrop ${isAnimating ? 'show' : ''}`}
        onClick={onClose}
      />
      
      {/* Bottom sheet drawer */}
      <div className={`citation-drawer ${isAnimating ? 'show' : ''}`}>
        {/* Handle bar for gesture indication */}
        <div className="citation-drawer-handle" />
        
        {/* Header */}
        <div className="citation-drawer-header">
          <h2 className="citation-drawer-title">
            {selectedCitation.title || 'Citation Source'}
          </h2>
          <button 
            className="citation-drawer-close"
            onClick={onClose}
            aria-label="Close citations"
          >
            ×
          </button>
        </div>

        {/* Navigation tabs for multiple citations */}
        {validCitations.length > 1 && (
          <div className="citation-drawer-tabs">
            {validCitations.map((citation, index) => (
              <button
                key={index}
                className={`citation-tab ${index === selectedCitationIndex ? 'active' : ''}`}
                onClick={() => onCitationSelect(index)}
              >
                <span className="citation-tab-number">{index + 1}</span>
                <span className="citation-tab-title">
                  {citation?.title || `Source ${index + 1}`}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="citation-drawer-content">
          <div 
            className="citation-content-text"
            dangerouslySetInnerHTML={{ __html: renderContent() }}
          />
          
          {selectedCitation.url && (
            <div className="citation-drawer-actions">
              <a 
                href={selectedCitation.url}
                target="_blank"
                rel="noopener noreferrer"
                className="citation-action-button primary"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15,3 21,3 21,9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                View Full Source
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CitationDrawer;